/**
 * User API – all user doc writes go through Cloud Functions (backend).
 * Passes actAsUserId when impersonating for operations on behalf of another user.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/config';
import { getActAsUserId } from '../impersonationStore';

const functions = getFunctions(app, 'us-central1');

function call(name, data = {}) {
  const fn = httpsCallable(functions, name);
  const actAs = getActAsUserId();
  const payload = actAs ? { ...data, actAsUserId: actAs } : data;
  return fn(payload);
}

/** Create or merge user doc (first sign-in or onboarding). Caller = current user. */
export async function userCreateOrUpdate(data) {
  const result = await call('userCreateOrUpdate', { data });
  return result.data;
}

/** Update user profile. Caller must be the user or an admin. */
export async function userUpdateProfile(userId, data) {
  const result = await call('userUpdateProfile', { userId, data });
  return result.data;
}

/** Backfill email on current user doc. */
export async function userBackfillEmail(email) {
  const result = await call('userBackfillEmail', { email });
  return result.data;
}

/**
 * Record app access: updates user's lastAccessedAt; writes audit entry if 30+ mins since last.
 * Call on app load when user is authenticated. Super admin sees all app_access in audit log.
 */
export async function recordAppAccess(profile) {
  const result = await call('recordAppAccess', {
    displayName: profile?.displayName ?? null,
    email: profile?.email ?? null,
    profile,
  });
  return result.data;
}

/** Block logins for a user (admin only). Disables Firebase Auth and revokes sessions; user sees blocked message on next sign-in. */
export async function blockUserLogin(userId) {
  const result = await call('blockUserLogin', { userId });
  return result.data;
}

/** Delete a user (admin only). Disables Firebase Auth, revokes sessions, deletes Firestore user doc. */
export async function deleteUser(userId) {
  const result = await call('deleteUser', { userId });
  return result.data;
}
